import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { userHasPermission } from '../../../../../swzutils/UserPermissionsUtil';
import { Button, notification, Skeleton, Tag } from "antd";
import { CloseOutlined, EditOutlined, LoadingOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faScaleBalanced } from "@fortawesome/free-solid-svg-icons";
import ActiveAtStatusTag from "../../../components/ActiveAtStatusTag";
import PaginatedFilterableTable, {} from "../../../components/table/PaginatedFilterableTable";
import PermissionButton from "../../../components/PermissionButton";
import PermissionDropdownButton from "../../../components/PermissionDropdownButton";
import { axiosErrorHandler, showErrorNotification } from "../../../../../swzutils";
import { useAppSelector } from "../../../../../store";
import { loggedInUserSelector, selectedTenantSelector } from "../../../../../store/slice/auth";
import { WholeNumberFormat } from "../../../components/FormattersTS";
import CustomerVehicleModal from "./CustomerVehicleModal";
import { DateTimeFormat } from "../../../components/FormattersTS";
import CustomerVehicleTareModal from "./CustomerVehicleTareModal";
import CustomerResource from "../api/CustomerResource";
import DateTimeModal from "../../../components/DateTimeModal";
import CustomerVehicleResource from "../api/CustomerVehicleResource";
const CustomerVehiclesTable = ({ customerId, onCustomerChanged }) => {
    const tableRef = useRef(null);
    const loggedInUser = useAppSelector(loggedInUserSelector);
    const selectedTenant = useAppSelector(selectedTenantSelector);
    const [notify, notificationContextHolder] = notification.useNotification();
    const [showAddUpdateModal, setShowAddUpdateModal] = useState(false);
    const [showDisableAtModal, setShowDisableAtModal] = useState(false);
    const [showTaresModal, setShowTaresModal] = useState(false);
    const [selectedCustomerVehicle, setSelectedCustomerVehicle] = useState(undefined);
    const [customer, setCustomer] = useState(null);
    useEffect(() => {
        CustomerResource.get(customerId, customer => {
            setCustomer(customer);
            onCustomerChanged(customer);
        }, axiosErrorHandler, () => { });
    }, [customerId]);
    const refresh = () => {
        tableRef.current?.fetch();
    };
    const onAddCustomerVehicleClicked = () => {
        if (selectedTenant && selectedTenant.id) {
            setShowAddUpdateModal(true);
            setSelectedCustomerVehicle(undefined);
        }
        else {
            showErrorNotification("Please select a profile to which you wish to add a customer vehicle too");
        }
    };
    const onEditCustomerVehicleClicked = (customerVehicle) => {
        setShowAddUpdateModal(true);
        setSelectedCustomerVehicle(customerVehicle);
    };
    const hideAddUpdateModal = () => {
        setShowAddUpdateModal(false);
        setSelectedCustomerVehicle(undefined);
    };
    const onShowTaresModal = (customerVehicle) => {
        setShowTaresModal(true);
        setSelectedCustomerVehicle(customerVehicle);
    };
    const hideTaresModal = () => {
        setShowTaresModal(false);
        setSelectedCustomerVehicle(undefined);
    };
    const onShowDisableAtModal = (customerVehicle) => {
        setShowDisableAtModal(true);
        setSelectedCustomerVehicle(customerVehicle);
    };
    const hideDisableAtModal = () => {
        setShowDisableAtModal(false);
        setSelectedCustomerVehicle(undefined);
    };
    const onDisableAtModalComplete = (dateTime) => {
        if (customer && selectedCustomerVehicle) {
            const notificationKey = `disable-${selectedCustomerVehicle.id}`;
            const notificationDescription = `${selectedCustomerVehicle.registration} - ${selectedCustomerVehicle.description}`;
            notify.open({
                key: notificationKey,
                title: 'Deactivating Vehicle',
                description: notificationDescription,
                duration: 0,
                icon: _jsx(LoadingOutlined, {})
            });
            CustomerVehicleResource.delete(customer.id, selectedCustomerVehicle.id, dateTime, updated => {
                tableRef.current?.updateRecord(updated);
                notify['success']({
                    key: notificationKey,
                    title: 'Vehicle Deactivated',
                    description: notificationDescription,
                    duration: 5
                });
            }, error => {
                axiosErrorHandler(error, errorMsge => {
                    notify['error']({
                        key: notificationKey,
                        title: 'Error Deactivating Vehicle',
                        description: errorMsge,
                        duration: 0
                    });
                });
            });
        }
        hideDisableAtModal();
    };
    const onVehicleAddedOrUpdated = (customerVehicle) => {
        if (selectedCustomerVehicle) {
            tableRef.current?.updateRecord(customerVehicle);
        }
        else {
            tableRef.current?.addRecord(customerVehicle);
        }
        hideAddUpdateModal();
    };
    const columns = [{
            key: "registration",
            title: 'Registration',
            dataIndex: 'registration',
            width: '5%',
            filter: {
                type: 'text',
                requestKey: 'registrationLike',
                placeholder: 'Rego',
            }
        }, {
            key: 'description',
            title: 'Description',
            dataIndex: 'description',
            width: '22%',
            filter: {
                type: 'text',
                requestKey: 'descriptionLike',
                placeholder: 'Description',
            }
        }, {
            title: 'Type',
            dataIndex: ['type', 'description'],
            width: '10%'
        }, {
            title: 'Category',
            dataIndex: ['type', 'category', 'description'],
            width: '10%',
            render: (text /*, record*/) => {
                return _jsx(Tag, { children: text });
            }
        }, {
            key: 'latestTare',
            title: 'Tare (tn)',
            dataIndex: ['latestTare', 'tareTonnes'],
            width: '5%',
            render: (text /*, record*/) => {
                return _jsx(WholeNumberFormat, { value: text });
            }
        }, {
            key: "latestTareEffectiveFrom",
            title: 'Effective',
            dataIndex: ['latestTare', 'effectiveFrom'],
            width: '10%',
            render: (text /*, record*/) => {
                return _jsx(DateTimeFormat, { value: text });
            }
        }, {
            title: 'Active',
            dataIndex: 'active',
            width: '5%',
            filter: {
                type: 'enum',
                requestKey: 'active',
                enums: [{
                        text: 'Active',
                        value: true,
                    }, {
                        text: 'Inactive',
                        value: false,
                    }],
                filterMultiple: false,
            },
            render: (text, record) => (_jsx(ActiveAtStatusTag, { inactiveAt: record.disabledAt }))
        }, {
            title: 'Action',
            key: 'action',
            width: '10%',
            condition: userHasPermission(loggedInUser, ['admin:customerVehicle:update']),
            render: (text, record) => (_jsx(PermissionDropdownButton, { permissions: ['admin:customerVehicle:update'], onClick: () => onEditCustomerVehicleClicked(record), label: _jsxs(_Fragment, { children: [_jsx(EditOutlined, {}), " Edit"] }), condition: !record.disabledAt, menuItems: [{
                        label: 'Tare',
                        key: 'customerVehicleTare',
                        icon: _jsx(FontAwesomeIcon, { icon: faScaleBalanced }),
                        condition: !record.disabledAt,
                        onClick: () => onShowTaresModal(record),
                        permissions: ['admin:customerVehicle:update'],
                    }, {
                        label: 'Deactivating',
                        key: 'customerVehicleDisable',
                        icon: _jsx(CloseOutlined, {}),
                        condition: !record.disabledAt,
                        onClick: () => onShowDisableAtModal(record),
                        permissions: ['admin:customerVehicle:delete'],
                    }] }))
        }];
    if (!customer) {
        return (_jsx(Skeleton, { active: true, loading: true, paragraph: { rows: 10 } }));
    }
    return (_jsxs(_Fragment, { children: [notificationContextHolder, _jsxs("div", { className: "table-operations clearfix", children: [_jsx(Button, { className: "pull-right", onClick: refresh, title: "Refresh", icon: _jsx(ReloadOutlined, {}) }), _jsx(PermissionButton, { permissions: ['admin:customerVehicle:create'], onClick: onAddCustomerVehicleClicked, icon: _jsx(PlusOutlined, {}), children: "Add a Customer Vehicle" })] }), _jsx(PaginatedFilterableTable, { tableKey: 'customerVehiclesTable', ref: tableRef, dataUri: `/admin/v1/customers/${customerId}/vehicles`, columns: columns, refreshOnSelectedTenantChanged: false, initialFilters: {
                    active: [true]
                } }), _jsx(CustomerVehicleModal, { open: showAddUpdateModal, customer: customer, customerVehicle: selectedCustomerVehicle, onUpdated: onVehicleAddedOrUpdated, onCancel: hideAddUpdateModal }), _jsx(CustomerVehicleTareModal, { open: showTaresModal && !!selectedCustomerVehicle, customer: customer, customerVehicle: selectedCustomerVehicle, onClose: hideTaresModal }), _jsx(DateTimeModal, { open: showDisableAtModal, title: "Disable Vehicle", description: "Select the date from which this vehicle will be disabled", onCancel: hideDisableAtModal, onComplete: onDisableAtModalComplete })] }));
};
export default CustomerVehiclesTable;
